import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, PlusCircle, X } from 'lucide-react';
import { biz } from '../../lib/api';
import { PriorityBadge, StatusBadge } from './BusinessLayout';

const BUCKETS = [
  { id: 'today', label: 'Today' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: 'week', label: 'This Week' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'all', label: 'All' },
];

export default function Followups() {
  const [bucket, setBucket] = useState('today');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    customer_name: '', responsible_name: '',
    due_date: new Date().toISOString().slice(0, 10),
    priority: 'medium', description: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await biz.listFollowups({ bucket })); }
    finally { setLoading(false); }
  }, [bucket]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.description || !form.responsible_name || !form.due_date) {
      toast.error('Description, person and due date required'); return;
    }
    try { await biz.createFollowup(form); toast.success('Follow-up created'); setShowForm(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const complete = async (id) => {
    try { await biz.completeFollowup(id, 'Done'); toast.success('Completed'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="grid gap-3" data-testid="biz-followups-root">
      <div className="flex items-center gap-2 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-black text-[#0D47A1]">Follow-ups</div>
          <h1 className="font-heading font-black text-2xl md:text-3xl">Follow-up Manager</h1>
        </div>
        <button className="ml-auto btn-primary" onClick={() => setShowForm(true)} data-testid="biz-followup-new-btn">
          <PlusCircle className="w-4 h-4"/>New Follow-up
        </button>
      </div>

      <div className="flex flex-wrap gap-1" data-testid="biz-followup-buckets">
        {BUCKETS.map((b) => (
          <button key={b.id} onClick={() => setBucket(b.id)}
            data-testid={`biz-followup-bucket-${b.id}`}
            className={`pill ${bucket === b.id ? '!bg-[#E67E22] !text-white !border-[#E67E22]' : '!bg-white'} text-xs`}>
            {b.label}
          </button>
        ))}
      </div>

      <div className="grid gap-2">
        {loading ? <div className="text-ink/50">Loading…</div>
          : !rows.length ? <div className="text-ink/50 text-sm">No follow-ups in this bucket.</div>
            : rows.map((r) => (
              <div key={r.id} className="card-soft p-3 flex items-center gap-3 flex-wrap" data-testid={`biz-followup-${r.followup_number}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">{r.followup_number}</span>
                    <PriorityBadge value={r.priority}/>
                    <StatusBadge value={r.status}/>
                  </div>
                  <div className="font-semibold text-sm mt-1">{r.customer_name || '—'}</div>
                  <div className="text-xs text-ink/60">Due {r.due_date} · {r.responsible_name}</div>
                  <div className="text-xs mt-1">{r.description}</div>
                </div>
                {r.status !== 'completed' && (
                  <button className="btn-primary" onClick={() => complete(r.id)}
                    data-testid={`biz-followup-complete-${r.followup_number}`}>
                    <CheckCircle2 className="w-4 h-4"/>Complete
                  </button>
                )}
              </div>
            ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-2" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-lg bg-white rounded-2xl border border-border shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="font-heading font-bold">New Follow-up</div>
              <button onClick={() => setShowForm(false)}><X className="w-4 h-4"/></button>
            </div>
            <div className="p-4 grid grid-cols-2 gap-2 text-sm">
              <L label="Customer name"><input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="biz-fu-form-customer"/></L>
              <L label="Responsible*"><input value={form.responsible_name} onChange={(e) => setForm({ ...form, responsible_name: e.target.value })} data-testid="biz-fu-form-resp"/></L>
              <L label="Due date*"><input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="biz-fu-form-date"/></L>
              <L label="Priority">
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="biz-fu-form-priority">
                  <option value="critical">Critical</option><option value="high">High</option>
                  <option value="medium">Medium</option><option value="low">Low</option>
                </select>
              </L>
              <div className="col-span-full"><L label="Description*"><textarea rows="2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="biz-fu-form-desc"/></L></div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button className="pill !bg-white" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn-primary" onClick={save} data-testid="biz-fu-form-save">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function L({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-widest text-ink/50">{label}</span>
      <span className="[&>*]:w-full [&>*]:rounded-lg [&>*]:border [&>*]:border-border [&>*]:px-2 [&>*]:py-1.5 [&>*]:bg-white">{children}</span>
    </label>
  );
}
